<template>
  <v-card flat tile class="d-flex flex-column folders-tree-card">
    <div class="gc-tree-scroll">
      <v-treeview
          :open="open"
          :active="active"
          :items="items"
          :search="filter"
          :load-children="readFolder"
          @update:open="openDir"
          item-key="path"
          item-text="basename"
          dense
          activatable
          transition
          class="folders-tree"
      >
        <template v-slot:prepend="{ item, open }">
          <v-icon v-if="item.type === 'dir'">
            {{ open ? 'mdi-folder-open-outline' : 'mdi-folder-outline' }}
          </v-icon>
          <v-icon v-else>
            {{ icons[item.extension.toLowerCase()] || icons['other'] }}
          </v-icon>
        </template>
        <template v-slot:label="{ item }">
          <div class="tree-node-label">
            <span
              class="tree-node-label__text"
              :title="item.basename"
              @click="onFolderClick(item)"
            >{{ item.basename }}</span>
            <v-btn
              v-if="item.type === 'dir'"
              icon
              x-small
              class="tree-node-label__refresh"
              @click.stop="readFolder(item)"
            >
              <v-icon small color="grey lighten-1">mdi-refresh</v-icon>
            </v-btn>
          </div>
        </template>
      </v-treeview>
    </div>
    <v-divider></v-divider>
    <v-toolbar dense flat class="shrink">
      <v-text-field
          solo
          flat
          hide-details
          label="Filter"
          v-model="filter"
          prepend-inner-icon="mdi-filter-outline"
          class="ml-n3"
      ></v-text-field>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn icon @click="init" v-on="on">
            <v-icon>mdi-collapse-all-outline</v-icon>
          </v-btn>
        </template>
        <span>Collapse All</span>
      </v-tooltip>
    </v-toolbar>
  </v-card>
</template>

<script>
export default {
  props: {
    icons: Object,
    storage: String,
    path: String,
    endpoints: Object,
    axios: Function,
    refreshPending: Boolean,
    /** File paths whose ancestor folders should be re-listed (e.g. after generate). */
    refreshFolderPaths: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      open: ['/'],
      active: [],
      items: [],
      filter: ""
    };
  },
  methods: {
    init() {
      this.open = [];
      this.items = [];

      setImmediate(() => {
        this.items = [
          {
            type: "dir",
            path: "/",
            basename: "root",
            extension: "",
            name: "root",
            children: []
          }
        ];

        setImmediate(() => {
          this.open = ['/'];
          this.active = ['/']
        });
      });

      if (this.path !== "/") {
        this.$emit("path-changed", "/");
      }
    },
    async openDir(open) {
      this.open = open
    },
    parentPathFor(targetPath) {
      const normalized = String(targetPath || '').replace(/\/+$/, '');
      const slash = normalized.lastIndexOf('/');
      if (slash <= 0) return '/';
      return `${normalized.substring(0, slash + 1)}`;
    },
    ancestorFolderPaths(filePath) {
      const folders = [];
      let dir = this.parentPathFor(filePath);
      while (dir) {
        folders.push(dir);
        if (dir === '/') break;
        const trimmed = dir.replace(/\/+$/, '');
        const slash = trimmed.lastIndexOf('/');
        dir = slash <= 0 ? '/' : `${trimmed.substring(0, slash + 1)}`;
      }
      return folders;
    },
    async refreshFoldersForFilePaths(filePaths) {
      const folderSet = new Set();
      for (const filePath of filePaths) {
        if (!filePath) continue;
        for (const folder of this.ancestorFolderPaths(filePath)) {
          folderSet.add(folder);
        }
      }
      const sorted = [...folderSet].sort(
        (a, b) => (a.match(/\//g) || []).length - (b.match(/\//g) || []).length
      );
      for (const folderPath of sorted) {
        const item = this.findItem(folderPath);
        if (item?.type === 'dir') {
          await this.readFolder(item);
        }
      }
    },

    async readFolder(item) {
      if (!item?.path) return;

      try {
        this.$emit('loading', true);

        const url = this.endpoints.list.url
          .replace(new RegExp('{storage}', 'g'), this.storage)
          .replace(new RegExp('{path}', 'g'), item.path);

        const response = await this.axios.request({
          url,
          method: this.endpoints.list.method || 'get'
        });

        item.children = response.data.map(child => {
          if (child.type === 'dir') {
            child.children = [];
          }
          return child;
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.$emit('loading', false);
      }
    },
    async onFolderClick(item) {
      await this.readFolder(item);
      this.$emit("path-changed", item?.path);
    },
    findItem(path) {
      let stack = [this.items[0]];

      while (stack.length > 0) {
        let node = stack.pop();
        if (node.path === path) {
          return node;
        } else if (node.children && node.children.length) {
          for (let i = 0; i < node.children.length; i++) {
            stack.push(node.children[i]);
          }
        }
      }

      return null;
    },
    refresh() {
      this.active = [this.path];

      if (!this.open.includes(this.path)) {
        this.open.push(this.path.substring(0, this.path.lastIndexOf('/')) + '/');
      }

      this.open = this.open.filter(path => this.path.indexOf(path) > -1);
      this.open = ['/', ...new Set(this.open)];

      setImmediate(() => {
        this.$emit('refreshed');
      });
    }
  },
  watch: {
    storage() {
      this.init();
    },
    async path(newPath) {
      if (newPath && newPath !== '/') {
        try {
          const item = this.findItem(newPath);
          if (item?.type === 'dir') {
            await this.readFolder(item);
          } else {
            const parent = this.findItem(this.parentPathFor(newPath));
            if (parent) await this.readFolder(parent);
          }
        } catch (error) {
          console.error('Error syncing tree for path:', error);
        }
      }
      this.refresh();
    },
    async refreshPending() {
      if (!this.refreshPending) return;

      const relatedPaths = [...(this.refreshFolderPaths || [])];
      const filePaths = [this.path, ...relatedPaths].filter(Boolean);

      try {
        await this.refreshFoldersForFilePaths(filePaths);
      } catch (error) {
        console.error('Error refreshing tree folders:', error);
      }

      this.refresh();
    }
  },
  created() {
    this.init();
  }
};
</script>

<style lang="scss" scoped>
.folders-tree-card {
  height: 100%;
  width: 100%;

  .tree-node-label {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    gap: 2px;

    &__text {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;
    }

    &__refresh {
      flex: 0 0 auto;
    }
  }

  ::v-deep .folders-tree {
    width: 100%;

    .v-treeview-node__root {
      min-width: 0;
    }

    .v-treeview-node__content {
      min-width: 0;
    }

    .v-treeview-node__label {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
    }

    .v-treeview-node {
      cursor: pointer;

      &:hover {
        background-color: rgba(255, 255, 255, 0.04);
      }
    }
  }
}
</style>